import styled from '@emotion/styled'
import {theme} from '../../features/Dashboard/theme'

// overflow-x: auto here forces the browser to compute overflow-y as auto
// too (per the CSS overflow spec, an axis can't stay "visible" once the
// other is scrollable) — so once the table actually needs to scroll
// horizontally, this wrapper also clips anything that pokes above/below its
// own edges. That's why tooltips/popovers anchored to cells (OptionsTooltip,
// StatusTooltip, FeedbackPopover) are rendered through a portal to
// document.body instead of as normal descendants — see ProposalTable/index.js.
export const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`

export const StyledTable = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
`

export const THead = styled.thead`
  border-bottom: 1px solid #b3c2f6;
  background: ${theme.colors.hover};

  &:hover,
  &:hover tr {
    border-bottom: 1px solid #b3c2f6;
    background: ${theme.colors.border};
  }
`

export const TBody = styled.tbody``

export const TR = styled.tr`
  background-color: ${theme.colors.bg};
  border-bottom: 1px solid ${theme.colors.border};

  &:hover {
    background: #fff7fc;
    border-bottom: 1px solid #fa9ab1;
  }
`

export const ExpiringDateCell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: default;

  &:hover > div {
    opacity: 1;
    pointer-events: auto;
  }
`

export const ExpiringTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #393939;
  border-radius: 12px;
  padding: 6px 10px;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 300;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #393939;
  }
`

export const ExpiringDateText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #c2410c;
`

export const TH = styled.th`
  padding: 8px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.text};
  white-space: nowrap;
  cursor: ${props => (props.sortable ? 'pointer' : 'default')};
  user-select: none;
  background: ${theme.colors.hover};

  &:hover {
    ${props => props.sortable && `color: ${theme.colors.text};`}
  }
`

export const THContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

export const SortArrows = styled.span`
  display: inline-flex;
  flex-direction: column;
  font-size: 8px;
  line-height: 1;
  color: ${theme.colors.placeholder};
  gap: 1px;
`

export const TD = styled.td`
  padding: 8px 16px;
  font-size: 13px;
  color: ${theme.colors.muted};
  white-space: nowrap;
`

export const BusinessNameText = styled.span`
  font-weight: 500;
  color: ${theme.colors.text};
`

export const OptionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`

export const EmptyStateTitle = styled.p`
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.muted};
`

export const EmptyStateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: linear-gradient(
    0deg,
    rgba(73, 70, 153, 1) 0%,
    rgba(37, 42, 100, 1) 100%
  );
  color: ${theme.colors.surface};
  border: none;
  border-radius: ${theme.radius.full};
  font-size: 14px;
  font-weight: 600;
  font-family: ${theme.font.family};
  cursor: pointer;
  transition: filter ${theme.transition.fast};

  &:hover {
    filter: brightness(1.12);
  }

  &:focus {
    outline: none;
  }
`

export const OptionsLabel = styled.span`
  position: relative;
  cursor: default;
`

// Rendered through a portal straight to document.body (see the ProposalTable
// component) — position: fixed with top/left/bottom set inline from the
// trigger's actual getBoundingClientRect(), so it's never clipped by
// TableWrapper's overflow (see TableWrapper's comment above). openDown
// flips the arrow to match whichever side the box actually opened on.
export const OptionsTooltip = styled.div`
  position: fixed;
  transform: translateX(-50%);
  background: #393939;
  border-radius: 12px;
  padding: 6px 8px;
  min-width: 120px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  white-space: normal;

  ${props =>
    props.openDown
      ? `
    &::after {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-bottom-color: #393939;
    }
  `
      : `
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #393939;
    }
  `}
`

export const OptionsTooltipItem = styled.p`
  margin: 0;
  padding: 4px 2px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '✓';
    font-size: 10px;
    font-weight: 700;
    color: #7b78e5;
    flex-shrink: 0;
    line-height: 1;
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }
`

export const StatusCell = styled.span`
  position: relative;
  display: inline-flex;
  cursor: default;
`

// Same portal approach as OptionsTooltip above.
export const StatusTooltip = styled.div`
  position: fixed;
  transform: translateX(-50%);
  background: #393939;
  border-radius: 12px;
  padding: 8px 10px;
  width: max-content;
  max-width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  white-space: normal;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;

  ${props =>
    props.openDown
      ? `
    &::after {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-bottom-color: #393939;
    }
  `
      : `
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #393939;
    }
  `}
`

export const FeedbackWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

export const FeedbackIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.primary};
  &:focus {
    outline: none;
  }
`

// Rendered through a portal straight to document.body (see the ProposalTable
// component) — position: fixed with top/left/bottom set inline from the
// feedback icon's actual getBoundingClientRect(), so it's never clipped by
// TableWrapper's overflow-driven clipping, even with a single table row.
// openDown is decided after mount (see the useLayoutEffect in index.js) by
// checking whether the default "open up" placement actually fits — mirrors
// how native/floating-ui tooltips flip.
export const FeedbackPopover = styled.div`
  position: fixed;
  transform: translateX(-50%);
  background: #393939;
  border-radius: 16px;
  padding: 14px 16px 14px;
  min-width: 240px;
  max-width: 300px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  white-space: normal;

  ${props =>
    props.openDown
      ? `
    &::after {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 8px solid transparent;
      border-bottom-color: #393939;
    }
  `
      : `
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 8px solid transparent;
      border-top-color: #393939;
    }
  `}
`

export const FeedbackPopoverText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
`

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

export const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${props =>
    props.disabled ? theme.colors.placeholder : theme.colors.muted};
  background: none;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  padding: 0;
  white-space: nowrap;
  transition: color 0.1s ease;

  &:hover:not([disabled]) {
    color: ${theme.colors.text};
  }

  &:active:not([disabled]) {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
  }
`

export const Spinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const LoadingSpinner = styled(Spinner)`
  width: 28px;
  height: 28px;
  border-width: 3px;
  color: ${theme.colors.primary};
`

export const DeleteBtn = styled(ActionBtn)`
  color: ${props => (props.disabled ? '#fca5a5' : '#ef4444')};

  & svg,
  & svg * {
    stroke: currentColor;
  }

  &:hover:not([disabled]) {
    color: ${theme.colors.danger};
  }
`
